import {
  Award, ChevronDown, Coins,
} from 'lucide-react';
import React, {
  useCallback, useEffect, useState,
} from 'react';
import styled from 'styled-components';

import { BonusBalanceResult } from '~/api/finance/financeApiTypes';
import {
  TextBoldStyle, TextRegStyle, TextSmallStyle,
} from '~/atomic/Typography';
import { lessThan } from '~/lib/mediaQuery';
import { numberFormatter } from '~/lib/numberFormatter';
import { pluralize } from '~/lib/pluralize';

const BASE_PERCENT = 5;
const SEGMENT_STEP = 200;
const TOTAL_SEGMENTS = 30;

const STATUSES = [
  {
    name: 'Старт', fullName: 'Стартовый статус', percent: 5,
  },
  {
    name: 'Бронза', fullName: 'Бронзовый статус', percent: 10,
  },
  {
    name: 'Серебро', fullName: 'Серебряный статус', percent: 15,
  },
  {
    name: 'Золото', fullName: 'Золотой статус', percent: 20,
  },
];

const SCALE_TICKS = [
  {
    percent: 5, label: 'Старт', position: 0,
  },
  {
    percent: 10, label: 'Бронза', position: 16.67,
  },
  {
    percent: 15, label: 'Серебро', position: 50,
  },
  {
    percent: 20, label: 'Золото', position: 100,
  },
];

const PERCENT_THRESHOLDS = [
  200, 400, 600, 800, 1000,
  1400, 1800, 2200, 2600, 3000,
  3600, 4200, 4800, 5400, 6000,
];

const GRADIENT_STOPS = [
  [96, 165, 250],
  [129, 140, 248],
  [167, 139, 250],
  [192, 132, 252],
];

function getSegmentColor(index: number): string {
  const t = index / (TOTAL_SEGMENTS - 1);
  const stopCount = GRADIENT_STOPS.length - 1;
  const segment = Math.min(Math.floor(t * stopCount), stopCount - 1);
  const local = (t * stopCount) - segment;
  const from = GRADIENT_STOPS[segment];
  const to = GRADIENT_STOPS[segment + 1];
  const r = Math.round(from[0] + (to[0] - from[0]) * local);
  const g = Math.round(from[1] + (to[1] - from[1]) * local);
  const b = Math.round(from[2] + (to[2] - from[2]) * local);
  return `rgb(${r}, ${g}, ${b})`;
}

const LS_KEY = 'bonus_widget_collapsed';

interface BonusWidgetProps {
  bonusBalance: BonusBalanceResult;
  collapsible?: boolean;
}

export const BonusWidget: React.FC<BonusWidgetProps> = ({ bonusBalance, collapsible }) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!collapsible) return;
    try {
      if (localStorage.getItem(LS_KEY) === '1') {
        setCollapsed(true);
      }
    } catch { /* noop */ }
  }, [collapsible]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_KEY, next ? '1' : '0');
      } catch { /* noop */ }
      return next;
    });
  }, []);

  const {
    available,
    accrual_percent,
    total_purchase_amount,
    expiring_soon,
  } = bonusBalance;

  const totalAmount = total_purchase_amount ?? 0;
  const currentPercent = accrual_percent ?? BASE_PERCENT;

  const filledCount = Math.min(
    Math.floor(totalAmount / SEGMENT_STEP),
    TOTAL_SEGMENTS,
  );

  const currentStatusIndex = [...STATUSES]
    .reverse()
    .findIndex((s) => currentPercent >= s.percent);
  const statusIndex = currentStatusIndex === -1
    ? 0
    : STATUSES.length - 1 - currentStatusIndex;
  const currentStatus = STATUSES[statusIndex];

  const nextPercent = currentPercent < 20 ? currentPercent + 1 : null;
  const nextThreshold = nextPercent !== null
    ? PERCENT_THRESHOLDS[nextPercent - BASE_PERCENT - 1]
    : null;
  const remaining = nextThreshold !== null
    ? Math.max(nextThreshold - totalAmount, 0)
    : 0;

  const getSegmentState = (index: number): 'filled' | 'current' | 'empty' => {
    if (filledCount === 0) return 'empty';
    if (index < filledCount - 1) return 'filled';
    if (index === filledCount - 1) return 'current';
    return 'empty';
  };

  const isTickActive = (tickIndex: number) => {
    const tick = SCALE_TICKS[tickIndex];
    const nextTick = SCALE_TICKS[tickIndex + 1];
    if (!nextTick) return currentPercent >= tick.percent;
    return currentPercent >= tick.percent && currentPercent < nextTick.percent;
  };

  const expiringSoonDate = (() => {
    if (!expiring_soon?.expires_at) return null;
    const d = new Date(expiring_soon.expires_at);
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  })();

  return (
    <SCContainer>
      <SCHeader
        as={collapsible ? 'button' : 'div'}
        onClick={collapsible ? toggleCollapsed : undefined}
        data-collapsible={collapsible || undefined}
        data-collapsed={collapsed || undefined}
      >
        <SCCoinsIcon size={20} strokeWidth={1.8} />
        <SCTitle>
          {numberFormatter(available)}
          {' '}
          {pluralize(available, 'бонус', 'бонуса', 'бонусов')}
        </SCTitle>
        {collapsible && <SCChevron data-collapsed={collapsed || undefined} />}
      </SCHeader>

      {!collapsed && (
        <>
          <SCDescription>
            1 бонус = 1 ₽. Накопленными бонусами можно оплатить до 50% стоимости любой книги. Чем больше покупок — тем выше процент кешбэка. Бонусы действуют 60 дней с момента начисления.  Бонусы нельзя использовать вместе со скидками: если вы оплачиваете книгу бонусами, скидка на неё не применяется.
            {' '}
            <SCRulesLink href="/legal/bonus-policy" target="_blank" rel="noopener noreferrer">
              Правила
            </SCRulesLink>
          </SCDescription>

          <SCStatusCard>
            <SCStatusCardLeft>
              <SCStatusIcon>
                <Award size={24} color="#fff" />
              </SCStatusIcon>
              <SCStatusInfo>
                <SCStatusName>{currentStatus.fullName}</SCStatusName>
                <SCStatusPercent>
                  Текущий кешбек:
                  {' '}
                  <span>
                    {currentPercent}
                    %
                  </span>
                </SCStatusPercent>
              </SCStatusInfo>
            </SCStatusCardLeft>
            <SCStatusBadges>
              {STATUSES.map((status, i) => {
                const isActive = i === statusIndex;
                return (
                  <SCBadgeGroup key={status.name} data-active={isActive || undefined}>
                    <SCBadgeCircle
                      data-active={isActive || undefined}
                      data-bronze={i === 1 || undefined}
                      data-gold={i === 3 || undefined}
                      data-past={i < statusIndex || undefined}
                    />
                    <SCBadgeLabel data-active={isActive || undefined}>
                      {status.name}
                    </SCBadgeLabel>
                  </SCBadgeGroup>
                );
              })}
            </SCStatusBadges>
          </SCStatusCard>

          {nextPercent !== null && remaining > 0 && (
            <SCProgressHint>
              До
              {' '}
              {nextPercent}
              % кешбека осталось:
              {' '}
              <span>
                {numberFormatter(remaining)}
                {' '}
                ₽
              </span>
            </SCProgressHint>
          )}

          <SCProgressWrap>
            <SCSegments>
              {Array.from({ length: TOTAL_SEGMENTS }, (_, i) => {
                const state = getSegmentState(i);
                let bg: string;
                if (state === 'current') {
                  bg = `linear-gradient(to right, ${PURPLE_600}, ${VIOLET_600})`;
                } else if (state === 'filled') {
                  bg = getSegmentColor(i);
                } else {
                  bg = SLATE_100;
                }

                return (
                  <SCSegment
                    key={i}
                    data-state={state}
                    data-first={i === 0 || undefined}
                    data-last={i === TOTAL_SEGMENTS - 1 || undefined}
                    style={{ background: bg }}
                  />
                );
              })}
            </SCSegments>
            <SCScaleRow>
              {SCALE_TICKS.map((tick, i) => (
                <SCTickGroup
                  key={tick.percent}
                  data-active={isTickActive(i) || undefined}
                  style={{
                    ...(i === 0 ? { left: 0 } : {}),
                    ...(i === SCALE_TICKS.length - 1 ? { right: 0 } : {}),
                    ...(i > 0 && i < SCALE_TICKS.length - 1
                      ? { left: `${tick.position}%`, transform: 'translateX(-50%)' }
                      : {}),
                  }}
                >
                  <SCTickMark data-active={isTickActive(i) || undefined} />
                  <SCTickLabel data-active={isTickActive(i) || undefined}>
                    <span>
                      {tick.percent}
                      %
                    </span>
                    <span>{tick.label}</span>
                  </SCTickLabel>
                </SCTickGroup>
              ))}
            </SCScaleRow>
          </SCProgressWrap>

          {expiring_soon?.amount ? (
            <SCExpiring>
              {expiringSoonDate ?? 'Скоро'}
              {' '}
              сгорят
              {' '}
              {expiring_soon.amount}
              {' '}
              {pluralize(expiring_soon.amount, 'бонус', 'бонуса', 'бонусов')}
            </SCExpiring>
          ) : null}
        </>
      )}
    </SCContainer>
  );
};

const VIOLET_500 = '#8b5cf6';
const VIOLET_600 = '#7c3aed';
const VIOLET_900 = '#4c1d95';
const PURPLE_600 = '#9333ea';
const SLATE_100 = '#f1f5f9';
const SLATE_400 = '#94a3b8';

const SCContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 24px;
  padding: 24px;

  ${lessThan('sm')} {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const SCHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  text-align: left;

  &[data-collapsible] {
    cursor: pointer;
  }

  &[data-collapsed] {
    margin-bottom: 0;
  }

  ${lessThan('sm')} {
    margin-bottom: 16px;

    &[data-collapsed] {
      margin-bottom: 0;
    }
  }
`;

const SCChevron = styled(ChevronDown)`
  width: 20px;
  height: 20px;
  color: ${SLATE_400};
  margin-left: auto;
  transition: transform 0.2s;
  flex-shrink: 0;

  &[data-collapsed] {
    transform: rotate(-90deg);
  }
`;

const SCCoinsIcon = styled(Coins)`
  color: ${VIOLET_500};
  flex-shrink: 0;
`;

const SCTitle = styled.span`
  ${TextBoldStyle};
  font-size: 18px;
  line-height: 24px;
  color: var(--black-color);

  ${lessThan('sm')} {
    font-size: 16px;
  }
`;

const SCDescription = styled.p`
  ${TextSmallStyle};
  color: var(--gray-color);
  margin: 0 0 16px;
`;

const SCRulesLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SCStatusCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(to right, #eff6ff, #f5f3ff, #faf5ff);
  border: 1px solid #ddd6fe;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;

  ${lessThan('sm')} {
    margin-bottom: 24px;
  }
`;

const SCStatusCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SCStatusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(to bottom right, #2563eb, #7c3aed, #9333ea);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const SCStatusInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SCStatusName = styled.div`
  ${TextBoldStyle};
  font-size: 16px;
  color: ${VIOLET_900};
`;

const SCStatusPercent = styled.div`
  ${TextRegStyle};
  font-size: 14px;
  color: #6d28d9;

  span {
    font-weight: 600;
  }
`;

const SCStatusBadges = styled.div`
  display: flex;
  gap: 4px;

  ${lessThan('sm')} {
    display: none;
  }
`;

const SCBadgeGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 56px;
  opacity: 0.4;

  &[data-active] {
    opacity: 1;
  }
`;

const SCBadgeCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom right, #cbd5e1, #94a3b8);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &::after {
    content: '';
    display: block;
    width: 14px;
    height: 14px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.329.124.5.5 0 0 1-.481-.593L8.52 12.89'/%3E%3Ccircle cx='12' cy='8' r='6'/%3E%3C/svg%3E") no-repeat center / contain;
  }

  &[data-past] {
    background: linear-gradient(to bottom right, #94a3b8, #64748b);
  }

  &[data-bronze] {
    background: linear-gradient(to bottom right, #cd7f32, #a0522d);
  }

  &[data-gold] {
    background: linear-gradient(to bottom right, #facc15, #ca8a04);
  }

  &[data-active] {
    background: linear-gradient(to bottom right, #2563eb, #7c3aed, #9333ea);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    outline: 2px solid ${VIOLET_500};
    outline-offset: 1px;
  }
`;

const SCBadgeLabel = styled.span`
  font-family: var(--second-font);
  font-size: 10px;
  font-weight: 500;
  color: #64748b;

  &[data-active] {
    color: ${VIOLET_900};
  }
`;

const SCProgressHint = styled.div`
  ${TextSmallStyle};
  color: #7c3aed;
  margin-bottom: 12px;

  span {
    font-weight: 600;
  }
`;

const SCProgressWrap = styled.div`
  position: relative;
  padding-bottom: 40px;
`;

const SCSegments = styled.div`
  display: flex;
  gap: 3px;

  ${lessThan('sm')} {
    gap: 2px;
  }
`;

const SCSegment = styled.div`
  flex: 1;
  height: 12px;

  &[data-first] {
    border-radius: 9999px 0 0 9999px;
  }

  &[data-last] {
    border-radius: 0 9999px 9999px 0;
  }

  &[data-state='current'] {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const SCScaleRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 12px;
`;

const SCTickGroup = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  &:first-child {
    align-items: flex-start;
  }

  &:last-child {
    align-items: flex-end;
  }

  &:not(:first-child):not(:last-child) {
    align-items: center;
  }
`;

const SCTickMark = styled.div`
  width: 2px;
  height: 8px;
  background: ${SLATE_400};

  &[data-active] {
    background: ${VIOLET_600};
  }
`;

const SCTickLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2px;

  span {
    font-family: var(--second-font);
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: #64748b;

    &:last-child {
      font-size: 10px;
      font-weight: 400;
      color: ${SLATE_400};
      margin-top: 2px;
    }
  }

  &[data-active] span {
    font-weight: 600;
    color: ${VIOLET_600};

    &:last-child {
      color: ${VIOLET_500};
    }
  }
`;

const SCExpiring = styled.div`
  ${TextRegStyle};
  font-size: 13px;
  color: #6d28d9;
  margin-top: 8px;
`;
